// 2026-11-01, Batch 7 Wave 2 dedupe — shared PDF layout helpers extracted
// verbatim from the 8 *Layout.ts modules (previously copy-pasted
// per-renderer). Drift found: none between the 8 layout copies (all
// byte-identical, git blob c0643049) — but the renderer-INLINED markdown
// helper drawRichText had drifted: certificatePdf.ts renders h1 at 13pt vs
// 14pt in referralPdf.ts / patientInfoPdf.ts. It is now parameterized
// (h1Size) with each call site passing its previous value, so every
// document still renders byte-identically. The 9th layout copy
// (dischargeSummaryLayout.ts) is out of scope for this wave and keeps its
// own copy; the 8 *Layout.ts siblings are now re-export shells over this
// module, so the *Pdf.ts renderers' import lines are unchanged.

import { jsPDF } from "jspdf";
import { fmtDatePdf } from "@emr/shared/india";
import { computeAgeYm, formatAgeLabel } from "@emr/shared/age";
import type { TaperStep } from "@emr/shared";

export const PAGE_MARGIN_MM = 10;
export const TEXT_INSET_MM = 10;
export const TEXT_X_MM = 10;
export const TEXT_WIDTH_MM = 190;
export const PAGE_HEIGHT_MM = 297;
export const PAGE_WIDTH_MM = 210;

const HEADER_PATH = "/assets/header.png";
const FOOTER_PATH = "/assets/footer.png";
/**
 * Letterhead area reserved when the PNG is missing/unreadable. The text
 * fallback header draws a box of exactly this height, so the text region
 * stays identical whether the image loaded or not.
 */
const HEADER_FALLBACK_MM = 22;
const FOOTER_FALLBACK_MM = 18;
/**
 * Caps so a near-square letterhead image can never eat the whole page.
 * If the cap bites, the width shrinks proportionally (still aspect-true)
 * and the rect is centered horizontally.
 */
const HEADER_MAX_MM = 80;
const FOOTER_MAX_MM = 40;

const MM_TO_PT = 2.834645669;

export interface TextRegion {
  x: number;
  y: number;
  width: number;
  maxHeight: number;
}

interface LetterheadImage {
  dataUrl: string;
  widthPx: number;
  heightPx: number;
}

// 2026-11-01 Batch 7 Wave 2: ONE shared module-level cache/state for all
// renderers (previously one per *Layout.ts copy). Key composition verified
// shared-safe: loadImage is only ever called with the two constants
// HEADER_PATH ("/assets/header.png") and FOOTER_PATH ("/assets/footer.png")
// — identical in all 8 copies (byte-identical files) — so a cached key
// always denotes the same letterhead image for every renderer.
const imageCache = new Map<string, LetterheadImage>();

/**
 * Heights (pt) of the letterhead areas as last drawn by drawHeader/drawFooter.
 * getTextRegion() and getSignatureLineY() read these, so body text always
 * starts below the header and stops above the footer whatever the images'
 * aspect ratios are.
 */
let headerHeightPt = mmToPt(HEADER_FALLBACK_MM);
let footerHeightPt = mmToPt(FOOTER_FALLBACK_MM);

async function loadImage(url: string): Promise<LetterheadImage | null> {
  const cached = imageCache.get(url);
  if (cached) return cached;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch ${url}: ${res.status}`);
    const blob = await res.blob();
    const dataUrl = await blobToDataURL(blob);
    const size = await measureImage(dataUrl);
    const img = { dataUrl, widthPx: size.width, heightPx: size.height };
    imageCache.set(url, img);
    return img;
  } catch {
    return null;
  }
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Natural pixel size of a data URL — used to keep the letterhead aspect-true. */
function measureImage(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = dataUrl;
  });
}

export function mmToPt(mm: number): number {
  return mm * MM_TO_PT;
}

/**
 * Fit a letterhead image into the full content width (page width minus the
 * two page margins) at its native aspect ratio: height = width / aspect.
 * Height is capped; when the cap bites, width shrinks to keep the ratio
 * exact and the rect is centered in the content width.
 */
function fitLetterhead(
  img: LetterheadImage,
  maxHeightMm: number,
): { x: number; w: number; h: number } {
  const boxW = mmToPt(PAGE_WIDTH_MM - 2 * PAGE_MARGIN_MM);
  const maxH = mmToPt(maxHeightMm);
  let w = boxW;
  let h = w * (img.heightPx / img.widthPx);
  if (h > maxH) {
    h = maxH;
    w = h * (img.widthPx / img.heightPx);
  }
  return { x: mmToPt(PAGE_MARGIN_MM) + (boxW - w) / 2, w, h };
}

export async function drawHeader(doc: jsPDF): Promise<void> {
  const y = mmToPt(PAGE_MARGIN_MM);
  const img = await loadImage(HEADER_PATH);
  if (img) {
    const { x, w, h } = fitLetterhead(img, HEADER_MAX_MM);
    try {
      doc.addImage(img.dataUrl, "PNG", x, y, w, h);
      headerHeightPt = h;
      return;
    } catch {
      // fall through to the text fallback below
    }
  }
  headerHeightPt = mmToPt(HEADER_FALLBACK_MM);
  drawTextHeader(
    doc,
    mmToPt(PAGE_MARGIN_MM),
    y,
    mmToPt(PAGE_WIDTH_MM - 2 * PAGE_MARGIN_MM),
    headerHeightPt,
  );
}

function drawTextHeader(doc: jsPDF, x: number, y: number, w: number, h: number) {
  doc.setFillColor(247, 240, 232);
  doc.rect(x, y, w, h, "F");
  doc.setTextColor(124, 45, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Clinic EMR", x + mmToPt(5), y + mmToPt(13));
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(107, 95, 88);
  doc.text("Clinic letterhead — place header.png at /assets/header.png", x + mmToPt(5), y + mmToPt(18));
}

export async function drawFooter(doc: jsPDF): Promise<void> {
  const img = await loadImage(FOOTER_PATH);
  if (img) {
    const { x, w, h } = fitLetterhead(img, FOOTER_MAX_MM);
    // Bottom-anchored: the footer's bottom edge sits on the page margin.
    const y = mmToPt(PAGE_HEIGHT_MM - PAGE_MARGIN_MM) - h;
    try {
      doc.addImage(img.dataUrl, "PNG", x, y, w, h);
      footerHeightPt = h;
      return;
    } catch {
      // skip
    }
  }
  footerHeightPt = mmToPt(FOOTER_FALLBACK_MM);
}

/**
 * Body-text region: starts 4mm below the header area, ends 4mm above the
 * footer area. Heights are whatever the last drawHeader/drawFooter actually
 * drew, so text never overlaps the letterhead at any aspect ratio.
 */
export function getTextRegion(doc: jsPDF): TextRegion {
  const headerBottom = mmToPt(PAGE_MARGIN_MM) + headerHeightPt;
  const footerTop = mmToPt(PAGE_HEIGHT_MM - PAGE_MARGIN_MM) - footerHeightPt;
  const y = headerBottom + mmToPt(4);
  return {
    x: mmToPt(TEXT_X_MM),
    y,
    width: mmToPt(TEXT_WIDTH_MM),
    maxHeight: footerTop - mmToPt(4) - y,
  };
}

/**
 * Y (pt, from the page top) for the signature rule. Anchored above the
 * footer with room for the three signatory text lines drawn below the
 * rule (name +4mm, qualifications +7mm, reg no +10mm) plus 2mm clearance.
 */
export function getSignatureLineY(doc: jsPDF): number {
  const footerTop = mmToPt(PAGE_HEIGHT_MM - PAGE_MARGIN_MM) - footerHeightPt;
  return footerTop - mmToPt(12);
}

export async function addPageWithHeader(doc: jsPDF): Promise<number> {
  doc.addPage();
  await drawHeader(doc);
  await drawFooter(doc);
  return getTextRegion(doc).y;
}

export function newPageY(doc: jsPDF, currentY: number, neededHeight: number): Promise<number> {

  return ensureSpace(doc, currentY, neededHeight);
}

export async function ensureSpace(doc: jsPDF, currentY: number, neededHeight: number): Promise<number> {
  const region = getTextRegion(doc);
  const bottomLimit = region.y + region.maxHeight;
  if (currentY + neededHeight > bottomLimit) {
    return addPageWithHeader(doc);
  }
  return currentY;
}

// ── Renderer-shared helpers (2026-11-01, Batch 7 Wave 2) ─────────────────────
// Previously inlined (private or exported) per renderer in prescriptionPdf.ts,
// visitSummaryPdf.ts, investigationPdf.ts, referralPdf.ts, certificatePdf.ts,
// patientInfoPdf.ts — proven body-identical before extraction (comment text
// varied between copies; code did not — except drawRichText's h1 size, see
// the header note). Kept verbatim, including each canonical copy's comments.

// ── Table ruling (2026-10-26; unified same day) ────────────────────────────
// One scheme shared by the diagnosis table and the medicine table: ALL table
// ruling — horizontal row dividers, outer frames, AND vertical column
// separators — prints at 0.5pt warm gray (150,138,128). The original 0.3pt
// #E1DAD2 ruling was effectively invisible on paper; an interim two-weight
// scheme (subtle 0.3pt verticals) was unified to this single visible weight
// per user request.
export const RULE_COLOR: [number, number, number] = [150, 138, 128];
export const RULE_WIDTH = 0.5;

export interface PrescriptionMedicine {
  serialNo: number;
  name: string;
  composition: string | null;
  strength: string | null;
  frequency: string | null;
  taper: TaperStep[] | null;
  duration: string | null;
  instructions: string | null;
}

export type InvestigationCategory = "Lab" | "Radiology" | "Electrophysiology";

/**
 * Draw a single logical line made of mixed bold/normal segments, wrapping at
 * word boundaries within the given box. align "left" starts at x and wraps
 * toward maxX; align "right" is right-justified to maxX and wraps toward it.
 * Returns the baseline Y of the last drawn line.
 */
export function drawMixedLine(
  doc: jsPDF,
  segments: { text: string; bold: boolean }[],
  x: number,
  y: number,
  maxX: number,
  lineH: number,
  size: number,
  align: "left" | "right" = "left",
): number {
  doc.setFontSize(size);
  // Measure every word with its own bold/normal font, then wrap greedily.
  const words: { text: string; bold: boolean; width: number }[] = [];
  for (const seg of segments) {
    doc.setFont("helvetica", seg.bold ? "bold" : "normal");
    for (const w of seg.text.split(/(\s+)/)) {
      if (w === "") continue;
      words.push({ text: w, bold: seg.bold, width: doc.getTextWidth(w) });
    }
  }
  const right = align === "right";
  const avail = maxX - x;
  const lines: { text: string; bold: boolean; width: number }[][] = [];
  let cur: typeof words = [];
  let cw = 0;
  for (const word of words) {
    if (word.text.trim() === "") {
      if (cur.length === 0) continue; // never start a line with a space
      cur.push(word);
      cw += word.width;
      continue;
    }
    if (cur.length > 0 && cw + word.width > avail) {
      lines.push(cur);
      cur = [word];
      cw = word.width;
    } else {
      cur.push(word);
      cw += word.width;
    }
  }
  if (cur.length > 0) lines.push(cur);

  let cy = y;
  for (const line of lines) {
    // Drop a trailing space so right-aligned text sits flush at maxX.
    const l = line[line.length - 1]?.text.trim() === "" ? line.slice(0, -1) : line;
    const lw = l.reduce((s, wd) => s + wd.width, 0);
    let cx = right ? maxX - lw : x;
    for (const word of l) {
      if (word.text.trim() === "") {
        cx += word.width; // spaces are not drawn but still advance
        continue;
      }
      doc.setFont("helvetica", word.bold ? "bold" : "normal");
      doc.text(word.text, cx, cy);
      cx += word.width;
    }
    cy += lineH;
  }
  return cy - lineH;
}

/**
 * Patient identity rows shared by the prescription info block and the
 * visit summary page 1. Rows are drawn at startY (no leading blank row —
 * callers position the block):
 * Row 1: "Name: {name}" (left) · "Date: {date}" (right) ·
 * Row 2: "Age: {age}, Sex: {sex}" (left) · "UHID: {uhid}" (right)
 * (descriptors bold, values normal; each side wraps within its half of the
 * width). Returns the Y one line-height below Row 2's baseline.
 */
export function drawPatientIdentityRows(
  doc: jsPDF,
  data: {
    patientName: string;
    clinicUhid: string;
    patientDob: string | null;
    patientGender: string | null;
    checkInAt: string;
  },
  startY: number,
  region?: { x: number; width: number },
): number {
  const r = region ?? getTextRegion(doc);
  let y = startY;
  const x = r.x;
  const w = r.width;
  // 1.5 line spacing for the 12pt patient details (18pt baseline-to-baseline).
  const lineH12 = 12 * 1.5;
  const midX = x + w / 2;
  const rightX = x + w;

  // Row 1 — Name: {name} on the left, Date: {date} on the right.
  const age = formatAgeLabel(computeAgeYm(data.patientDob));
  doc.setTextColor(26, 20, 19);
  const r1l = drawMixedLine(doc, [
    { text: "Name: ", bold: true },
    { text: data.patientName, bold: false },
  ], x, y, midX - mmToPt(6), lineH12, 12);
  const r1r = drawMixedLine(doc, [
    { text: "Date: ", bold: true },
    { text: fmtDatePdf(data.checkInAt), bold: false },
  ], midX + mmToPt(6), y, rightX, lineH12, 12, "right");
  y = Math.max(r1l, r1r) + lineH12;

  // Row 2 — Age: {age}, Sex: {sex} on the left, UHID: {uhid} on the right.
  const ageSexSegments: { text: string; bold: boolean }[] = [];
  if (age) ageSexSegments.push({ text: "Age: ", bold: true }, { text: age, bold: false });
  if (data.patientGender) {
    if (ageSexSegments.length > 0) ageSexSegments.push({ text: ", ", bold: false });
    ageSexSegments.push({ text: "Sex: ", bold: true }, { text: data.patientGender, bold: false });
  }
  const r2l = drawMixedLine(doc, ageSexSegments, x, y, midX - mmToPt(6), lineH12, 12);
  const r2r = drawMixedLine(doc, [
    { text: "UHID: ", bold: true },
    { text: data.clinicUhid, bold: false },
  ], midX + mmToPt(6), y, rightX, lineH12, 12, "right");
  y = Math.max(r2l, r2r) + lineH12;

  return y;
}

/**
 * Classifies a catalog test's category into a print-sheet bucket.
 * Radiology and Electrophysiology get their own single-column sheets;
 * every other category — including null and unrecognized free-form text —
 * falls back to the Lab sheet. The catalog's category column is free-form
 * text (investigation_categories registry, 2026-09-19), so an exhaustive
 * whitelist here would silently drop tests whose category doesn't match
 * (see SCHEMA.md §18, 2026-09-20 postmortem). Matching is case-insensitive
 * and whitespace-trimmed ("RADIOLOGY" === "Radiology").
 */
export function classifyInvestigation(category: string | null | undefined): InvestigationCategory {
  const c = (category ?? "").trim().toLowerCase();
  if (c === "radiology") return "Radiology";
  if (c === "electrophysiology") return "Electrophysiology";
  return "Lab";
}

/**
 * Renders a numbered 2-column table for Radiology / Electrophysiology
 * investigations. The serial number ("1.", "2.", …) sits to the LEFT of
 * the table on the same row (aligned with the top of the table), and
 * the table occupies the remaining width:
 *
 *   1. ┌─────────────────┬──────────────────────────┐
 *      │ Investigation   │ MRI Brain                │
 *      │ Region & Side   │ Right knee               │
 *      │ Clinical Notes  │ History of trauma…       │
 *      └─────────────────┴──────────────────────────┘
 *   2. ┌─────────────────┬──────────────────────────┐
 *      │ …               │ …                        │
 *      └─────────────────┴──────────────────────────┘
 *
 * Inside the table, Col 1 is 25% of the table width (labels) and Col 2 is
 * 75% (values). Row 2 (Region & Side) and Row 3 (Clinical Notes) are
 * omitted when their values are null/empty — only the Investigation row
 * always shows.
 *
 * Table background colours match the prescription table exactly:
 *   - Col 1 (labels): header-band fill (247,240,232), bold maroon text (124,45,18)
 *   - Col 2 (values): zebra fill (252,248,243) on odd investigations
 *   - Borders: (225,218,210), 0.3pt
 *   - Font: 12pt throughout
 */
async function renderDetailsTable(
  doc: jsPDF,
  x: number,
  startY: number,
  tableWidth: number,
  tests: { id: string; name: string }[],
  details: Record<string, { regionAndSide: string; clinicalNotes: string }>,
): Promise<number> {
  const TABLE_FONT = 12;
  const cellPad = mmToPt(2);
  const lineH = mmToPt(8.5); // double spacing for 12pt rows (matches prescription table)
  const base = mmToPt(6); // text baseline from row top (double-spaced)
  const blockGap = mmToPt(5); // gap between investigation blocks
  const borderCol = [225, 218, 210] as const;
  const headerFill = [247, 240, 232] as const; // prescription header band
  const zebraFill = [252, 248, 243] as const; // prescription odd-row zebra
  const headerTextCol = [124, 45, 18] as const; // maroon for labels + serial
  const valueTextCol = [26, 20, 19] as const; // near-black for values

  // Serial number column — narrow, sits to the left of the table.
  // Wide enough for "99." at 12pt bold (~8mm) with a small right gap.
  const serialColW = mmToPt(10);
  const tableX = x + serialColW; // table starts after the serial number
  const tableW = tableWidth - serialColW; // table shrinks to make room

  // Col 1 = 25% of table width (labels), Col 2 = 75% (values).
  const col1W = tableW * 0.25;
  const col2W = tableW * 0.75;

  let y = startY;

  for (let i = 0; i < tests.length; i++) {
    const test = tests[i];
    const detail = details[test.id];
    const regionAndSide = detail?.regionAndSide?.trim() || null;
    const clinicalNotes = detail?.clinicalNotes?.trim() || null;

    // Build the rows for this investigation. Row 1 (Investigation) is always
    // shown. Row 2 (Region & Side) is omitted if null/empty. Row 3 (Clinical
    // Notes) is omitted if null/empty.
    const rows: { label: string; value: string; wrapWidth: number; boldValue: boolean }[] = [];
    rows.push({ label: "Investigation", value: test.name, wrapWidth: col2W - cellPad - mmToPt(1), boldValue: true });
    if (regionAndSide) {
      rows.push({ label: "Region & Side", value: regionAndSide, wrapWidth: col2W - cellPad - mmToPt(1), boldValue: false });
    }
    if (clinicalNotes) {
      rows.push({ label: "Clinical Notes", value: clinicalNotes, wrapWidth: col2W - cellPad - mmToPt(1), boldValue: false });
    }

    // Measure row heights (each row may wrap) at 12pt.
    doc.setFontSize(TABLE_FONT);
    const rowHeights: number[] = rows.map((r) => {
      doc.setFont("helvetica", r.boldValue ? "bold" : "normal");
      const lines = doc.splitTextToSize(r.value, r.wrapWidth) as string[];
      return base + lineH * (lines.length - 1) + mmToPt(2.5);
    });
    const blockH = rowHeights.reduce((s, h) => s + h, 0);
    const totalH = blockH + blockGap;

    // Page break if the serial number + table doesn't fit.
    y = await ensureSpace(doc, y, totalH);

    // Serial number — bold, 12pt, maroon, aligned with the first row's baseline.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(TABLE_FONT);
    doc.setTextColor(...headerTextCol);
    doc.text(`${i + 1}.`, x, y + base);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...valueTextCol);

    // Col 1 background — prescription header-band fill (247,240,232) across
    // the full height of this investigation's table block.
    doc.setFillColor(...headerFill);
    doc.rect(tableX, y, col1W, blockH, "F");

    // Col 2 zebra — odd investigations get the cream tint (252,248,243).
    if (i % 2 === 1) {
      doc.setFillColor(...zebraFill);
      doc.rect(tableX + col1W, y, col2W, blockH, "F");
    }

    // Draw rows.
    let rowY = y;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      // Label (col 1) — bold, 12pt, maroon, top-aligned.
      doc.setFont("helvetica", "bold");
      doc.setFontSize(TABLE_FONT);
      doc.setTextColor(...headerTextCol);
      doc.text(row.label, tableX + cellPad, rowY + base);

      // Value (col 2) — bold for the Investigation row (test name stands out),
      // normal for Region & Side / Clinical Notes. 12pt, near-black, top-aligned,
      // word-wrapped.
      doc.setFont("helvetica", row.boldValue ? "bold" : "normal");
      doc.setFontSize(TABLE_FONT);
      doc.setTextColor(...valueTextCol);
      const valueLines = doc.splitTextToSize(row.value, row.wrapWidth) as string[];
      for (let li = 0; li < valueLines.length; li++) {
        doc.text(valueLines[li], tableX + col1W + cellPad, rowY + base + lineH * li);
      }
      rowY += rowHeights[r];
    }

    // Borders — outer rect + vertical separator + horizontal row separators.
    doc.setDrawColor(...borderCol);
    doc.setLineWidth(0.3);
    doc.rect(tableX, y, tableW, blockH);
    // Vertical separator between col 1 and col 2.
    doc.line(tableX + col1W, y, tableX + col1W, y + blockH);
    // Horizontal row separators (not at top/bottom — the outer rect handles those).
    let sepY = y;
    for (let r = 0; r < rows.length - 1; r++) {
      sepY += rowHeights[r];
      doc.line(tableX, sepY, tableX + tableW, sepY);
    }

    y += totalH;
  }

  return y;
}

/**
 * Renders one category on the current page (header/footer already drawn).
 * Shared by the single-category sheet, the combined multi-page sheet, and
 * the visit summary's appended investigation pages.
 *
 * For Lab: 2-column dot grid (unchanged).
 * For Radiology / Electrophysiology: numbered 2-column table per investigation:
 *   Col 1 (25% width): labels — "Investigation", "Region & Side", "Clinical Notes"
 *   Col 2 (75% width): values — test name, region & side, clinical notes
 * Rows where both Region & Side AND Clinical Notes are null/empty are omitted
 * (only the investigation name row is always shown). Table background colors
 * match the prescription table: header fill (247,240,232), zebra (252,248,243),
 * borders (225,218,210).
 */
export async function renderCategoryPage(doc: jsPDF, data: {
  patientName: string;
  clinicUhid: string;
  patientDob: string | null;
  patientGender: string | null;
  checkInAt: string;
  diagnosis: string | null;
  category: InvestigationCategory;
  tests: { id: string; name: string }[];
  notes: string | null;
  doctorName: string | null;
  doctorRegNumber: string | null;
  investigationDetails?: Record<string, { regionAndSide: string; clinicalNotes: string }>;
}): Promise<void> {
  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  // Patient details block — one blank row below the letterhead, then the
  // shared identity rows (Row 1 Name/Date, Row 2 Age+Sex/UHID — 12pt, bold
  // descriptors), then the diagnosis on row 3 on the left (bold descriptor,
  // normal value, word-wrapped across the full text width).
  y = drawPatientIdentityRows(doc, data, y + mmToPt(5));
  const diag = data.diagnosis?.trim();
  if (diag) {
    // drawMixedLine word-wraps and returns the Y of the LAST line it drew.
    // Advance y past that last line so the separator, category title, and
    // test grid below move down in lockstep with however many lines the
    // diagnosis wraps to (was fixed at 12*1.5 = one line, which overlapped
    // multi-line diagnoses and the content below).
    const lastLineY = drawMixedLine(doc, [
      { text: "Diagnosis: ", bold: true },
      { text: diag, bold: false },
    ], x, y, x + w, 12 * 1.5, 12);
    y = lastLineY + 12 * 1.5;
  }

  // Thick separator.
  y += mmToPt(3);
  doc.setDrawColor(124, 45, 18);
  doc.setLineWidth(1.2);
  doc.line(x, y, x + w, y);
  y += mmToPt(7);

  // Category title.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(124, 45, 18);
  doc.text(`${data.category} Investigations`, x, y);
  y += mmToPt(7);

  // Tests grid — solid round dot before each test instead of a checkbox.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(26, 20, 19);

  const filtered = data.tests;
  if (filtered.length === 0) {
    doc.text("No tests selected.", x, y);
    y += mmToPt(5);
  } else if (data.category === "Lab") {
    // 2-column grid.
    const cols = 2;
    const colW = w / cols;
    const rowH = mmToPt(6);
    for (let i = 0; i < filtered.length; i++) {
      if (i % cols === 0) {
        y = await ensureSpace(doc, y, rowH);
      }
      const col = i % cols;
      const cx = x + col * colW + mmToPt(2);
      // Solid round dot (1.2mm diameter), vertically centered on the text
      // line, horizontally centered in the slot the checkbox used to occupy.
      doc.setFillColor(26, 20, 19);
      doc.circle(cx + mmToPt(2), y - mmToPt(1.1), mmToPt(0.6), "F");
      const lines = doc.splitTextToSize(filtered[i].name, colW - mmToPt(7));
      doc.text(lines[0] ?? filtered[i].name, cx + mmToPt(5), y);
      if (i % cols === cols - 1 || i === filtered.length - 1) {
        y += rowH;
      }
    }
    y += mmToPt(2);
  } else {
    // Radiology / Electrophysiology — numbered 2-column table per test:
    //   Col 1 (25%): labels (Investigation, Region & Side, Clinical Notes)
    //   Col 2 (75%): values (test name, region & side, clinical notes)
    // Rows where both Region & Side AND Clinical Notes are null/empty are
    // omitted (only the Investigation row always shows). Table colors match
    // the prescription table: header fill (247,240,232), zebra (252,248,243),
    // borders (225,218,210).
    y = await renderDetailsTable(doc, x, y, w, filtered, data.investigationDetails ?? {});
  }

  y += mmToPt(3);

  // Notes.
  if (data.notes) {
    y = await ensureSpace(doc, y, mmToPt(10));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(60, 50, 45);
    doc.text("Notes:", x, y);
    y += mmToPt(5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(26, 20, 19);
    const noteLines = doc.splitTextToSize(data.notes, w);
    for (const line of noteLines) {
      y = await ensureSpace(doc, y, mmToPt(5));
      doc.text(line, x, y);
      y += mmToPt(5);
    }
  }

  // Signatory block (bottom-right, anchored above the footer).
  const signY = getSignatureLineY(doc);
  const signX = x + w - mmToPt(60);
  doc.setDrawColor(200, 190, 180);
  doc.line(signX, signY, signX + mmToPt(50), signY);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 20, 19);
  doc.text(data.doctorName ?? "Doctor", signX + mmToPt(50), signY + mmToPt(4), { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 95, 88);
  if (data.doctorRegNumber) {
    doc.text(`Reg No: ${data.doctorRegNumber}`, signX + mmToPt(50), signY + mmToPt(7), { align: "right" });
  }
}

/**
 * Render a markdown-rendered HTML string (renderMarkdown output) as plain
 * jsPDF text blocks — headings, bullets, ordered lists, paragraphs.
 * h1Size is per call site (Batch 7 Wave 2 parameterization of the one
 * drift found between the three former copies): referralPdf and
 * patientInfoPdf pass 14, certificatePdf passes 13 — each call site's
 * previous literal value.
 */
export async function drawRichText(
  doc: jsPDF,
  startY: number,
  x: number,
  w: number,
  html: string,
  h1Size: number,
): Promise<number> {
  let y = startY;
  const blocks = html.split(/\n+/).filter(Boolean);
  for (const block of blocks) {
    const headingMatch = /^<h([1-3])>(.*)<\/h[1-3]>$/.exec(block);
    const ulMatch = /^<ul>(.*)<\/ul>$/.exec(block);
    const olMatch = /^<ol>(.*)<\/ol>$/.exec(block);
    const paraMatch = /^<p>(.*)<\/p>$/.exec(block);

    if (headingMatch) {
      const level = parseInt(headingMatch[1], 10);
      const size = level === 1 ? h1Size : level === 2 ? 12 : 11;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(size);
      doc.setTextColor(124, 45, 18);
      const text = stripTags(headingMatch[2]);
      const lines = doc.splitTextToSize(text, w);
      for (const line of lines) {
        y = await ensureSpace(doc, y, mmToPt(5));
        doc.text(line, x, y);
        y += mmToPt(5);
      }
      y += mmToPt(1);
    } else if (ulMatch || olMatch) {
      const items = (ulMatch ? ulMatch[1] : olMatch![1])
        .split(/<\/?li>/)
        .filter((s) => s.trim().length > 0);
      const bullet = ulMatch ? "•" : "";
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(26, 20, 19);
      let idx = 1;
      for (const item of items) {
        const prefix = ulMatch ? `${bullet}  ` : `${idx}.  `;
        const text = stripTags(item);
        const lines = doc.splitTextToSize(text, w - mmToPt(6));
        for (let li = 0; li < lines.length; li++) {
          y = await ensureSpace(doc, y, mmToPt(5));
          doc.text((li === 0 ? prefix : "   ") + lines[li], x + mmToPt(2), y);
          y += mmToPt(5);
        }
        idx++;
      }
      y += mmToPt(1);
    } else if (paraMatch) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(26, 20, 19);
      const inner = paraMatch[1].replace(/<br\/>/g, "\n");
      const paragraphs = inner.split("\n");
      for (const para of paragraphs) {
        const text = stripTags(para);
        const lines = doc.splitTextToSize(text, w);
        for (const line of lines) {
          y = await ensureSpace(doc, y, mmToPt(5));
          doc.text(line, x, y);
          y += mmToPt(5);
        }
      }
      y += mmToPt(2);
    }
  }
  return y;
}

export function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}